import { COMMON_RATIOS, type AspectRatio } from "@/data/aspectRatios";

export function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
}

/** Solve missing dimension. Pass null/0 for the unknown. */
export function solveDimension(opts: {
  ratioW: number;
  ratioH: number;
  width?: number | null;
  height?: number | null;
}): { width: number; height: number } | null {
  const { ratioW, ratioH, width, height } = opts;
  if (!ratioW || !ratioH) return null;
  if (width && !height) return { width, height: (width * ratioH) / ratioW };
  if (height && !width) return { width: (height * ratioW) / ratioH, height };
  if (width && height) return { width, height };
  return null;
}

export interface RatioInfo {
  w: number;
  h: number;
  label: string;
  decimal: number;
  match?: AspectRatio;          // exact / nearest common ratio
  matchExact: boolean;
  commonName?: { en: string; bn: string };
  resolutionName?: string;      // "Full HD / FHD"
}

const RESOLUTION_NAMES: Record<string, string> = {
  "640x360":   "nHD",
  "854x480":   "FWVGA",
  "1280x720":  "HD / 720p",
  "1366x768":  "WXGA",
  "1920x1080": "Full HD / FHD",
  "2560x1440": "QHD / 2K",
  "3840x2160": "4K UHD",
  "7680x4320": "8K UHD",
  "1024x768":  "XGA",
  "1080x1080": "Square 1080",
  "1080x1920": "Mobile FHD",
  "3440x1440": "UWQHD (Ultrawide)",
  "2560x1080": "UWFHD (Ultrawide)",
};

/** Compute simplified ratio + match against COMMON_RATIOS. */
export function findRatio(width: number, height: number): RatioInfo | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  const g = gcd(width, height);
  let w = width / g;
  let h = height / g;
  // Avoid silly ratios like 427:240 — round to nearest common if very close.
  const decimal = width / height;

  // 1) try exact (gcd) match against COMMON_RATIOS using w/h ratio (e.g. 16:9)
  let match = COMMON_RATIOS.find((r) => r.w === w && r.h === h);
  let matchExact = !!match;

  // 2) otherwise try nearest by decimal value within 0.5%
  if (!match) {
    const candidates = COMMON_RATIOS.map((r) => ({ r, diff: Math.abs(r.decimal - decimal) }));
    candidates.sort((a, b) => a.diff - b.diff);
    const best = candidates[0];
    if (best && best.diff / decimal < 0.005) {
      match = best.r;
      matchExact = false;
      // collapse the displayed ratio to the common form
      w = best.r.w;
      h = best.r.h;
    }
  }

  const resolutionName = RESOLUTION_NAMES[`${Math.round(width)}x${Math.round(height)}`];

  return {
    w, h,
    label: match ? match.label : `${w}:${h}`,
    decimal,
    match,
    matchExact,
    commonName: match?.name,
    resolutionName,
  };
}

export function area(w: number, h: number): number {
  return w * h;
}

export { COMMON_RATIOS };
export type { AspectRatio };
